import { useEffect, useState } from "react";

import { useProjects } from "../../api/projects.js";
import { useRunningTimer, useStartTimer, useStopTimer } from "../../api/time-tracking.js";
import { Button } from "../ui/button.js";

/**
 * Floating timer widget — Feature #5.
 *
 * Lives in the global header. Polls `useRunningTimer` (30 s) so a
 * timer started in another tab / device shows up. While running:
 * shows elapsed HH:MM:SS, project name, stop button. Idle: shows a
 * "Start timer" button which expands a quick form (project select +
 * description + billable toggle).
 *
 * Idle warning: when a timer's been running > 8 h, the widget gets a
 * red border + "Idle?" badge. Server-side `autoStopRunaways`
 * eventually cleans up at 24 h.
 */

const IDLE_WARNING_SECONDS = 8 * 3600;

export function TimerWidget() {
  const { data: running } = useRunningTimer();
  const start = useStartTimer();
  const stop = useStopTimer();
  const [showStart, setShowStart] = useState(false);

  if (running?.entry) {
    return (
      <RunningPanel entry={running.entry} onStop={() => stop.mutate()} stopping={stop.isPending} />
    );
  }
  if (showStart) {
    return (
      <StartForm
        starting={start.isPending}
        onStart={(input) => start.mutate(input, { onSuccess: () => setShowStart(false) })}
        onCancel={() => setShowStart(false)}
      />
    );
  }
  return (
    <Button size="sm" variant="ghost" onClick={() => setShowStart(true)}>
      ▶ Start timer
    </Button>
  );
}

// ─── Running panel ──────────────────────────────────────

function RunningPanel({
  entry,
  onStop,
  stopping,
}: {
  entry: { id: string; startAt: string; description: string | null; projectId: string | null };
  onStop: () => void;
  stopping: boolean;
}) {
  const elapsed = useElapsedSeconds(entry.startAt);
  const isIdle = elapsed > IDLE_WARNING_SECONDS;
  return (
    <div
      className={`flex items-center gap-2 rounded-md border px-3 py-1.5 ${
        isIdle ? "border-fg-danger bg-info-bg" : "border-strong bg-surface"
      }`}
    >
      <span className="text-sm font-mono text-fg" aria-label="Elapsed time">
        {formatHMS(elapsed)}
      </span>
      {isIdle && <span className="text-xs font-semibold text-fg-danger">Idle?</span>}
      {entry.description && (
        <span className="max-w-32 truncate text-xs text-fg-muted" title={entry.description}>
          {entry.description}
        </span>
      )}
      <Button size="sm" variant="ghost" onClick={onStop} disabled={stopping}>
        {stopping ? "Stopping…" : "■ Stop"}
      </Button>
    </div>
  );
}

function useElapsedSeconds(startAt: string): number {
  // Lazy init — react-hooks/purity rejects Date.now() in render but
  // accepts it inside a useState initializer or useEffect callback.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);
  const startMs = Date.parse(startAt);
  return Math.max(0, Math.floor((now - startMs) / 1_000));
}

function formatHMS(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

// ─── Start form ──────────────────────────────────────────

function StartForm({
  starting,
  onStart,
  onCancel,
}: {
  starting: boolean;
  onStart: (input: { projectId?: string | null; description?: string; billable: boolean }) => void;
  onCancel: () => void;
}) {
  const { data: projectsList } = useProjects();
  const [projectId, setProjectId] = useState("");
  const [description, setDescription] = useState("");
  const [billable, setBillable] = useState(true);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onStart({
      projectId: projectId || null,
      description: description.trim() || undefined,
      billable,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex items-center gap-2 rounded-md border border-strong bg-surface px-3 py-1.5"
    >
      <select
        value={projectId}
        onChange={(e) => setProjectId(e.target.value)}
        className="rounded-md border border-strong bg-surface px-2 py-1 text-xs"
      >
        <option value="">Project…</option>
        {(projectsList ?? []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What are you working on?"
        className="w-48 rounded-md border border-strong bg-surface px-2 py-1 text-xs"
      />
      <label className="flex items-center gap-1 text-xs text-fg">
        <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
        billable
      </label>
      <Button type="submit" size="sm" disabled={starting}>
        {starting ? "Starting…" : "▶ Start"}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
        ✕
      </Button>
    </form>
  );
}
